import Button from 'components/actions/Button';
import type { Scenario } from 'features/simulations-v2/types';
import { useCallback, useMemo, useState } from 'react';

import CreateSimulationDialog from './CreateSimulationDialog';
import DeleteSimulationDialog from './DeleteSimulationDialog';
import useListDomainSimulations from './hooks/useListDomainSimulations';
import useSimulationActions from './hooks/useSimulationActions';
import SimulationDetailSidebar from './SimulationDetailSidebar';
import SimulationsTable from './SimulationsTable';

const LIST_POLL_INTERVAL_MS = 5000;

interface Props {
    scenarios: Scenario[] | undefined;
    scenariosIsLoading: boolean;
    scenariosIsError: boolean;
}

const SimulationsTab = ({ scenarios, scenariosIsLoading, scenariosIsError }: Props) => {
    const {
        simulations,
        isLoading: simIsLoading,
        isError: simIsError,
        refresh,
    } = useListDomainSimulations(LIST_POLL_INTERVAL_MS);
    const isSimInitialLoad = simIsLoading && simulations === undefined;
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [selectedSimulationVin, setSelectedSimulationVin] = useState<string | null>(null);
    const [simulationVinToDelete, setSimulationVinToDelete] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const scenarioNames = useMemo(() => new Map((scenarios ?? []).map(s => [s.id, s.name])), [scenarios]);

    const { start, remove, setConnectivity, triggerMrm, writeAttributes, pinAttribute, unpinAttribute } =
        useSimulationActions({ onSuccess: refresh });

    const handleSelectSimulation = useCallback((vin: string) => {
        setSelectedSimulationVin(prev => (prev === vin ? null : vin));
    }, []);

    const handleCloseSidebar = useCallback(() => setSelectedSimulationVin(null), []);

    const handleDeleteRequest = useCallback((vin: string) => {
        setSimulationVinToDelete(vin);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!simulationVinToDelete) return;
        setIsDeleting(true);
        await remove(simulationVinToDelete);
        if (selectedSimulationVin === simulationVinToDelete) setSelectedSimulationVin(null);
        setIsDeleting(false);
        setSimulationVinToDelete(null);
    }, [simulationVinToDelete, selectedSimulationVin, remove]);

    const handleToggleConnectivity = useCallback(
        async (vin: string) => {
            const simulation = simulations?.find(s => s.vin === vin);
            if (!simulation) return;
            await setConnectivity(vin, !simulation.isOnline);
        },
        [simulations, setConnectivity]
    );

    const handleCreateSuccess = useCallback(() => {
        setIsCreateDialogOpen(false);
        refresh();
    }, [refresh]);

    return (
        <>
            <div className='DomainSimulationPage__tabContent'>
                <div className='DomainSimulationPage__tabHeader'>
                    <div>
                        <h5 className='DomainSimulationPage__tabTitle'>Simulations</h5>
                        <p>Overview of all currently active domain simulations</p>
                    </div>
                    <Button
                        data-testid='domain-sim-create-button'
                        style='primary'
                        onClick={() => setIsCreateDialogOpen(true)}
                    >
                        <span className='rioglyph rioglyph-plus' />
                        Create simulation
                    </Button>
                </div>
            </div>
            <div>
                <SimulationsTable
                    simulations={simulations}
                    scenarioNames={scenarioNames}
                    isLoading={isSimInitialLoad || scenariosIsLoading}
                    isError={simIsError || scenariosIsError}
                    selectedSimulationVin={selectedSimulationVin}
                    refresh={refresh}
                    onSelectVin={handleSelectSimulation}
                    onStart={start}
                    onDelete={handleDeleteRequest}
                    onToggleConnectivity={handleToggleConnectivity}
                    onTriggerMrm={triggerMrm}
                />
            </div>
            <SimulationDetailSidebar
                vin={selectedSimulationVin}
                isOpen={selectedSimulationVin !== null}
                onClose={handleCloseSidebar}
                onStart={() => selectedSimulationVin && start(selectedSimulationVin)}
                onDelete={() => selectedSimulationVin && handleDeleteRequest(selectedSimulationVin)}
                onToggleConnectivity={() => selectedSimulationVin && handleToggleConnectivity(selectedSimulationVin)}
                onTriggerMrm={() => selectedSimulationVin && triggerMrm(selectedSimulationVin)}
                onWriteAttributes={writeAttributes}
                onPinAttribute={pinAttribute}
                onUnpinAttribute={unpinAttribute}
            />
            <DeleteSimulationDialog
                vin={simulationVinToDelete}
                isLoading={isDeleting}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setSimulationVinToDelete(null)}
            />
            <CreateSimulationDialog
                show={isCreateDialogOpen}
                scenarios={scenarios ?? []}
                onClose={() => setIsCreateDialogOpen(false)}
                onSuccess={handleCreateSuccess}
            />
        </>
    );
};

export default SimulationsTab;
